import type { IImage } from '@/services/models/Media/Image';
import type { SwatchHexValue } from '@/utils/color-utils';
import type { Nullable } from '@/type-utils';
import type { CSSProperties } from 'react';

/**
 * Creates a CSS style object for color swatch backgrounds based on hex values.
 * Supports single colors, multi-color gradients, and image-based swatches.
 *
 * @param hexValue - Hex color value (single string or array for gradients).
 * @param primaryImage - Optional image to use instead of hex color.
 * @returns CSS properties object for the swatch.
 *
 * @example
 * ```ts
 * // Single color
 * generateSwatchBackgroundStyles('#FF0000') // { backgroundColor: '#FF0000' }
 *
 * // Multi-color gradient
 * generateSwatchBackgroundStyles(['#FF0000', '#0000FF']) // { backgroundImage: 'linear-gradient(...)' }
 *
 * // With image
 * generateSwatchBackgroundStyles(null, { src: 'image.jpg' }) // { backgroundColor: '#fff', backgroundImage: 'url(...)' }
 * ```
 */
export function generateSwatchBackgroundStyles(
  hexValue: Nullable<SwatchHexValue>,
  primaryImage?: Nullable<IImage>
): CSSProperties {
  // Primary image takes precedence
  if (primaryImage) {
    return {
      backgroundColor: '#fff',
      backgroundImage: `url(${primaryImage.src})`
    };
  }

  // Handle array for multi-color split gradient
  if (Array.isArray(hexValue) && hexValue.length >= 2) {
    return {
      backgroundImage: `linear-gradient(-45deg, ${hexValue[0]} 0%, ${hexValue[0]} 50%, ${hexValue[1]} 50%, ${hexValue[1]} 50%)`
    };
  }

  // Single color (string or array with 1 element)
  const singleColor = Array.isArray(hexValue)
    ? (hexValue[0] ?? '#fff')
    : (hexValue ?? '#fff');

  return { backgroundColor: singleColor };
}
